import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";

import { PropertyValueMap, css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Style } from "./Styles";
import { BaseElement } from "./BaseElement";
import { parseGeoCoordinates } from "../ParseGeoCoordinate";
import { keyed } from "lit/directives/keyed.js";
import { litIf } from "./LitUtils";
import { IPlaceFilter } from "../backend/cloud/PlaceFilterTable";
import { PlaceTable } from "../backend/cloud/PlaceTable";

import Autocomplete from "./AutoComplete/Autocomplete";
import "./AutoComplete/Autocomplete";
import { v4 as uuidv4 } from "uuid";
import countries from "i18n-iso-countries";
import { IPlace } from "../models/Models";
import SlInput from "@shoelace-style/shoelace/dist/components/input/input.js";
import { formatLocation } from "../Format";
import { ILocation } from "../models/ILocation";
import orderBy from "lodash-es/orderBy.js";

import { levenshteinDistance, makeAsyncThrottle } from "../Utils";
import { LightTheme } from "./ThemeLight";
import { IUserInfo, UserTable } from "../backend/cloud/UserTable";

enum AutoCompleteType {
    Place,
    Location,
    Address,
    User,
}

type AutoCompleteResult = {
    type: AutoCompleteType;
    text: string;
    context: any;
    id: string;
    weight: number;
};

@customElement("ds-search-bar")
export class SearchBar extends BaseElement {
    static override styles = [
        Style,
        LightTheme,
        css`
            :host {
                display: flex;
                gap: 0.5rem;
                justify-content: center;
            }

            .container {
                font-size: 1.5rem;
                display: flex;
                align-items: center;
                flex-direction: horizontal;
                gap: 0.5rem;
                justify-content: center;
                width: 100%;
            }

            sl-input {
                flex: 1;
                min-width: 0;
            }

            sl-input::part(base) {
                border: var(--ds-border);
            }
        `,
    ];

    @property({ type: Object }) filter: IPlaceFilter | undefined =
        this._kernel.currentPlaceFilter.get();
    @property({ type: Boolean }) goToButton = false;
    @property({ type: Boolean }) addPlaceButton = false;
    @property({ type: String }) size: "small" | "medium" | "large" = "medium";
    @property({ type: String }) placeholder = "Search by place name, id or geo coords";

    @property({ type: Boolean }) excludeLocationResults = false;
    @property({ type: Boolean }) excludePlaceResults = false;
    @property({ type: Boolean }) excludeSearchResults = false;
    @property({ type: Boolean }) excludeUserResults = false;

    @property({ type: Boolean }) disabled = false;

    @query("#autocomplete") _autocompleteControl?: Autocomplete;
    @query("#input") _inputControl?: SlInput;

    @state() protected _results: AutoCompleteResult[] = [];

    private _places = this._kernel.dataStore.getSource<PlaceTable>("places");
    private _users = this._kernel.dataStore.getSource<UserTable>("users");
    private _throttledUpdate = makeAsyncThrottle(this._autocomplete.bind(this), 250);

    private _googleService?: google.maps.places.AutocompleteService;
    private _placesService?: google.maps.places.PlacesService;

    protected override firstUpdated(
        _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
    ): void {
        const googleDiv = this.shadowRoot?.getElementById("google-dummy-div") as HTMLDivElement;
        this._placesService = new google.maps.places.PlacesService(googleDiv);
        this._googleService = new google.maps.places.AutocompleteService();
    }

    private async _getUserResults(text: string): Promise<AutoCompleteResult[]> {
        let results: AutoCompleteResult[] = [];
        if (text) {
            const all = await this._users.all();
            for (const user of all) {
                const name = user.displayName;
                if (name?.toLocaleLowerCase().includes(text)) {
                    results.push({
                        type: AutoCompleteType.User,
                        text: name,
                        context: user,
                        id: uuidv4(),
                        weight: levenshteinDistance(text, name),
                    });
                }
            }
        }

        results = orderBy(results, ["weight"], ["asc"]);

        return results;
    }

    private _getLocationResult(text: string): AutoCompleteResult | undefined {
        const location = parseGeoCoordinates(text);
        if (location) {
            return {
                type: AutoCompleteType.Location,
                text: formatLocation(location),
                context: location,
                id: uuidv4(),
            } as AutoCompleteResult;
        }
        return undefined;
    }

    private async _getPlaceResults(text: string): Promise<AutoCompleteResult[]> {
        let results = [];
        if (text) {
            const all = await this._places.all();
            for (const place of all) {
                if (!place.data.locationKnown) {
                    continue;
                }
                let matchText = "";
                if (place.id === text) {
                    matchText = place.data.name;
                } else if (place.data.name.toLocaleLowerCase().includes(text)) {
                    matchText = place.data.name;
                } else if (place.data.spelioData.systemName?.toLocaleLowerCase().includes(text)) {
                    matchText = `${place.data.name}, ${place.data.spelioData.systemName}`;
                } else {
                    for (const name of place.data.alternateNames) {
                        if (name.toLocaleLowerCase().includes(text)) {
                            matchText = name;
                            break;
                        }
                    }
                }

                if (matchText.length > 0) {
                    let country = place.data.address?.country;
                    if (country) {
                        country = countries.getName(country, "en", { select: "alias" });
                    }
                    const title = [matchText, place.data.address?.state, country]
                        .filter((x) => !!x)
                        .join(", ");

                    results.push({
                        type: AutoCompleteType.Place,
                        text: title,
                        context: place,
                        id: uuidv4(),
                        weight: levenshteinDistance(text, matchText),
                    });
                }
            }
        }
        results = orderBy(results, ["weight"], ["asc"]);

        // move entries that start with the search text to the top
        const prefixed = [];
        const unPrefixed = [];
        for (const result of results) {
            if (result.text.toLocaleLowerCase().startsWith(text)) {
                prefixed.push(result);
            } else {
                unPrefixed.push(result);
            }
        }
        return [...prefixed, ...unPrefixed];
    }

    private async _getSearchSuggestions(text: string): Promise<AutoCompleteResult[] | undefined> {
        if (text.length > 2) {
            try {
                const results = await new Promise<google.maps.places.AutocompletePrediction[]>(
                    (resolve, reject) => {
                        this._googleService?.getPlacePredictions(
                            { input: text },
                            (predictions, status) => {
                                if (
                                    status != google.maps.places.PlacesServiceStatus.OK ||
                                    !predictions
                                ) {
                                    reject();
                                } else {
                                    resolve(predictions);
                                }
                            }
                        );
                    }
                );

                if (results) {
                    return orderBy(
                        results.map((x) => {
                            return {
                                type: AutoCompleteType.Address,
                                id: uuidv4(),
                                text: x.description,
                                context: x,
                                weight: levenshteinDistance(text, x.description),
                            } as AutoCompleteResult;
                        }),
                        ["weight"],
                        ["asc"]
                    );
                }
            } catch (_e) {
                //no-op
            }
        }

        return undefined;
    }

    private _clearResults() {
        this._results = [];
    }

    private _autocompleteTypeToIconName(type: AutoCompleteType) {
        switch (type) {
            case AutoCompleteType.Place:
                return "geo-fill";
            case AutoCompleteType.Location:
                return "geo-alt-fill";
            case AutoCompleteType.Address:
                return "map-fill";
            case AutoCompleteType.User:
                return "person-fill";
        }
    }
    private async _autocomplete(text: string) {
        if (this._autocompleteControl) {
            text = text.toLocaleLowerCase().trim();

            let results: AutoCompleteResult[] = [];

            if (!this.excludeLocationResults) {
                const locationResult = this._getLocationResult(text);
                if (locationResult) {
                    results.push(locationResult);
                }
            }

            if (results.length === 0) {
                if (!this.excludePlaceResults) {
                    results = [...results, ...(await this._getPlaceResults(text))];
                }

                if (!this.excludeSearchResults) {
                    const suggestions = await this._getSearchSuggestions(text);
                    if (suggestions) {
                        results = [...results, ...suggestions];
                    }
                }
            }

            const users = await this._getUserResults(text);
            if (users) {
                results = [...results, ...users];
            }

            results = orderBy(results, ["weight"], ["asc"]);

            this._results = results.slice(0, 10);
        }
    }

    override render() {
        return html`<div class="container">
            <div id="google-dummy-div" style="display:none"></div>
            ${litIf(
                this.goToButton,
                () =>
                    html`<sl-tooltip content="Go to location">
                        <sl-icon-button
                            name="compass"
                            @click=${() => this._raiseEvent("ds-input-coords")}
                        ></sl-icon-button>
                    </sl-tooltip>`
            )}
            <ds-autocomplete
                id="autocomplete"
                @sl-select=${async (e: Event) => {
                    const item = this._results?.find(
                        (x) => x.id === (e as CustomEvent).detail.item.value
                    );
                    if (item) {
                        switch (item.type) {
                            case AutoCompleteType.User:
                                {
                                    const user = item.context as IUserInfo;
                                    this._raiseEvent("ds-open-user-profile", {
                                        id: user.owner,
                                    });
                                }
                                break;
                            case AutoCompleteType.Place:
                                {
                                    const place = item.context as IPlace;
                                    this._raiseEvent("ds-view-on-map", {
                                        placeId: place.id,
                                        location: place.data.location,
                                    });
                                }
                                break;
                            case AutoCompleteType.Location:
                                {
                                    const location = item.context as ILocation;
                                    this._raiseEvent("ds-go-to-location", {
                                        location,
                                        zoom: 17,
                                    });
                                }
                                break;
                            case AutoCompleteType.Address:
                                {
                                    const place =
                                        item.context as google.maps.places.AutocompletePrediction;

                                    this._placesService?.getDetails(
                                        {
                                            placeId: place.place_id,
                                            fields: ["geometry"],
                                        },
                                        (
                                            result: google.maps.places.PlaceResult | null,
                                            status: google.maps.places.PlacesServiceStatus
                                        ) => {
                                            if (
                                                result &&
                                                result.geometry &&
                                                status === google.maps.places.PlacesServiceStatus.OK
                                            ) {
                                                this._raiseEvent("ds-go-to-location", {
                                                    location: {
                                                        lat: result.geometry?.location?.lat(),
                                                        lon: result.geometry?.location?.lng(),
                                                    },
                                                    zoom: 17,
                                                });
                                            }
                                        }
                                    );
                                }
                                break;
                        }
                        if (this._inputControl) {
                            this._inputControl.value = "";
                            this._clearResults();
                        }
                    }
                }}
            >
                <sl-input
                    ?disabled=${this.disabled}
                    id="input"
                    slot="trigger"
                    size=${this.size}
                    autocomplete="off"
                    clearable
                    pill
                    placeholder=${this.placeholder}
                    value=${keyed(this.filter, this.filter?.filter.nameOrIdQuery ?? "")}
                    @sl-input=${async (e: any) => {
                        await this._throttledUpdate(e.target.value);
                    }}
                >
                    <sl-icon name="search" slot="prefix"></sl-icon
                ></sl-input>
                ${this._results?.map(
                    (x) =>
                        html`<sl-menu-item value="${x.id}"
                            ><sl-icon
                                slot="prefix"
                                name="${this._autocompleteTypeToIconName(x.type)}"
                            ></sl-icon
                            >${x.text}</sl-menu-item
                        >`
                )}
            </ds-autocomplete>
            ${litIf(
                this.addPlaceButton,
                () =>
                    html` <sl-tooltip content="Create new place">
                        <sl-icon-button
                            name="plus-circle"
                            @click=${() => this._raiseEvent("ds-add-place")}
                        ></sl-icon-button>
                    </sl-tooltip>`
            )}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-search-bar": SearchBar;
    }
}
